// src/components/resources/ResourcesHero.js
// Premium hero section for Resources page with editorial aesthetic

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import './styles/resources-hero.css';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const chipVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Quick filter topics
const QUICK_TOPICS = [
  { id: 'genai', label: 'AI & GenAI', icon: '✦' },
  { id: 'supply-chain', label: 'Supply Chain', icon: '◈' },
  { id: 'manufacturing', label: 'Manufacturing', icon: '▣' },
  { id: 'agentic', label: 'Agentic AI', icon: '◉' },
  { id: 'document', label: 'Document Intelligence', icon: '◇' },
];

const ResourcesHero = ({ activeTopics = [], onTopicClick }) => {
  const prefersReducedMotion = useReducedMotion();

  const handleTopicClick = (topicId) => {
    if (onTopicClick) {
      // Map display label back to actual topic name
      const topic = QUICK_TOPICS.find((t) => t.id === topicId);
      if (topic) {
        onTopicClick(topic.label);
      }
    }
  };

  const isTopicActive = (topicId) => {
    const topic = QUICK_TOPICS.find((t) => t.id === topicId);
    return topic && activeTopics.includes(topic.label);
  };

  return (
    <section className="resources-hero">
      {/* Background elements */}
      <div className="resources-hero__bg">
        <div className="resources-hero__gradient" />
        <div className="resources-hero__noise" />
        <div className="resources-hero__grid" />

        {/* Floating orbs */}
        <div className="resources-hero__orb resources-hero__orb--1" />
        <div className="resources-hero__orb resources-hero__orb--2" />
        <div className="resources-hero__orb resources-hero__orb--3" />
      </div>

      <div className="resources-hero__container">
        <motion.div
          className="resources-hero__content"
          variants={prefersReducedMotion ? {} : containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Eyebrow */}
          <motion.div
            className="resources-hero__eyebrow"
            variants={prefersReducedMotion ? {} : itemVariants}
          >
            <Sparkles size={14} />
            <span>Knowledge Center</span>
          </motion.div>

          {/* Main heading */}
          <motion.h1
            className="resources-hero__title"
            variants={prefersReducedMotion ? {} : itemVariants}
          >
            <span className="resources-hero__title-line">
              Insights &
            </span>
            <span className="resources-hero__title-line resources-hero__title-line--accent">
              Resources
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="resources-hero__subtitle"
            variants={prefersReducedMotion ? {} : itemVariants}
          >
            Deep dives into AI-powered operations, manufacturing intelligence,
            and supply chain optimization. Expert perspectives from the
            Rosalytics team.
          </motion.p>

          {/* Quick filter chips */}
          <motion.div
            className="resources-hero__chips"
            variants={prefersReducedMotion ? {} : itemVariants}
          >
            <span className="resources-hero__chips-label">Explore by topic:</span>
            <div className="resources-hero__chips-row">
              {QUICK_TOPICS.map((topic, index) => (
                <motion.button
                  key={topic.id}
                  className={`resources-hero__chip ${
                    isTopicActive(topic.id) ? 'resources-hero__chip--active' : ''
                  }`}
                  onClick={() => handleTopicClick(topic.id)}
                  variants={prefersReducedMotion ? {} : chipVariants}
                  whileHover={
                    prefersReducedMotion
                      ? {}
                      : { scale: 1.05, y: -2 }
                  }
                  whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                  custom={index}
                >
                  <span className="resources-hero__chip-icon">{topic.icon}</span>
                  <span className="resources-hero__chip-label">{topic.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Decorative line */}
        <motion.div
          className="resources-hero__divider"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </section>
  );
};

export default ResourcesHero;
